import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Award,
  GraduationCap,
  Building2,
  Users,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  FileCheck,
  Shield,
  Star,
  Phone,
  Mail,
  MapPin,
  Send,
  UserPlus,
  Building,
  Briefcase,
  Target,
  Handshake,
  School
} from 'lucide-react';

const CertificacionesPage = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('interesados');
  const [loading, setLoading] = useState(false);

  // Form states
  const [interesadoForm, setInteresadoForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    ciudad: '',
    tipo_interes: 'curso_upim',
    mensaje: ''
  });

  const [centroForm, setCentroForm] = useState({
    nombre_centro: '',
    representante: '',
    email: '',
    telefono: '',
    ciudad: '',
    estado: '',
    sitio_web: '',
    descripcion: '',
    experiencia_certificacion: ''
  });

  const [certificadorForm, setCertificadorForm] = useState({
    nombre: '',
    email: '',
    telefono: '',
    ciudad: '',
    numero_conocer: '',
    especialidad: '',
    experiencia_anios: '',
    ya_registrado_conocer: false,
    mensaje: ''
  });

  const handleInteresadoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/certifications/interesados', interesadoForm);
      toast({
        title: "¡Solicitud enviada!",
        description: "Nos pondremos en contacto contigo pronto.",
      });
      setInteresadoForm({
        nombre: '',
        email: '',
        telefono: '',
        ciudad: '',
        tipo_interes: 'curso_upim',
        mensaje: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar la solicitud",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const handleCentroSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/certifications/centros', centroForm);
      toast({
        title: "¡Solicitud enviada!",
        description: "Revisaremos tu solicitud y te contactaremos.",
      });
      setCentroForm({
        nombre_centro: '',
        representante: '',
        email: '',
        telefono: '',
        ciudad: '',
        estado: '',
        sitio_web: '',
        descripcion: '',
        experiencia_certificacion: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar la solicitud",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const handleCertificadorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/certifications/certificadores', certificadorForm);
      toast({
        title: "¡Solicitud enviada!",
        description: "Revisaremos tu perfil y te contactaremos.",
      });
      setCertificadorForm({
        nombre: '',
        email: '',
        telefono: '',
        ciudad: '',
        numero_conocer: '',
        especialidad: '',
        experiencia_anios: '',
        ya_registrado_conocer: false,
        mensaje: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar la solicitud",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  return (
    <Layout>
      {/* Hero Section - Órgano Certificador SEP */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* SEP Badge */}
            <div className="inline-flex items-center justify-center mb-6">
              <Badge className="px-6 py-2 bg-white/10 text-white border border-white/20 text-base font-medium">
                <Shield className="h-5 w-5 mr-2" />
                Órgano Certificador Avalado ante la SEP
              </Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="certificaciones-title">
              Certificaciones y <br className="hidden sm:block" />
              <span className="text-gray-300">Formación Académica</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              UPIM trabaja de la mano con <strong className="text-white">Centros Certificadores</strong> y ayuda a crear nuevos centros con profesionales interesados en la <strong className="text-white">formación académica</strong> de nuestra profesión.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100 h-14 px-8 text-lg"
                onClick={() => setActiveTab('interesados')}
                data-testid="btn-me-interesa"
              >
                <UserPlus className="mr-2 h-5 w-5" />
                Me interesa certificarme
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-lg"
                onClick={() => setActiveTab('centros')}
                data-testid="btn-soy-centro"
              >
                <Building2 className="mr-2 h-5 w-5" />
                Soy Centro Certificador
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">500+</p>
              <p className="text-sm text-gray-600">Profesionales certificados</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">15+</p>
              <p className="text-sm text-gray-600">Años de experiencia</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">10+</p>
              <p className="text-sm text-gray-600">Centros aliados</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">98%</p>
              <p className="text-sm text-gray-600">Tasa de aprobación</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Certificaciones */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
              <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
              Oferta Académica
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Nuestras opciones de formación
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ofrecemos diversas rutas de certificación y formación para profesionales inmobiliarios en todas las etapas de su carrera.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Academia UPIM */}
            <Card className="border-2 border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-300 group">
              <CardHeader className="pb-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <School className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Academia UPIM</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Cursos y talleres exclusivos para socios, impartidos por expertos del sector inmobiliario mexicano.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Masterclasses semanales gratuitas</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Talleres prácticos presenciales</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Certificados de participación</span>
                  </li>
                </ul>
                <Badge variant="outline" className="w-full justify-center py-2 border-gray-300 text-gray-700">
                  Incluido en membresía
                </Badge>
              </CardContent>
            </Card>

            {/* Colegio de Inmobiliarios */}
            <Card className="border-2 border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-300 group">
              <CardHeader className="pb-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Building className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Colegio de Inmobiliarios</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Programas especializados del Colegio Nacional de Profesionales Inmobiliarios de México.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Diplomados especializados</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Cursos de actualización</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Descuentos exclusivos afiliados</span>
                  </li>
                </ul>
                <Badge variant="outline" className="w-full justify-center py-2 border-gray-300 text-gray-700">
                  15-25% descuento afiliados
                </Badge>
              </CardContent>
            </Card>

            {/* Certificaciones CONOCER */}
            <Card className="border-2 border-gray-200 hover:border-gray-400 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gray-900 text-white text-xs font-bold px-3 py-1">
                SEP
              </div>
              <CardHeader className="pb-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-gray-900">Certificaciones CONOCER</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Estándares de competencia laboral avalados por la SEP a través del CONOCER.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">EC0110.02 Asesoría inmobiliaria</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">EC0111 Valuación de inmuebles</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">EC0442 Administración condominios</span>
                  </li>
                </ul>
                <Badge className="w-full justify-center py-2 bg-gray-900 hover:bg-gray-800">
                  Reconocimiento oficial SEP
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Para Profesionales Interesados */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/10 text-white border border-white/20">
                <Target className="h-3.5 w-3.5 mr-1.5" />
                Para Formadores
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                ¿Te interesa la formación académica inmobiliaria?
              </h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                UPIM busca trabajar de la mano con profesionales interesados en esta vertiente de nuestra profesión. Si quieres convertirte en <strong className="text-white">evaluador certificador CONOCER</strong> o crear tu propio <strong className="text-white">Centro de Evaluación</strong>, podemos ayudarte.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4 bg-white/5 rounded-xl p-4">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Conviértete en Evaluador</h4>
                    <p className="text-sm text-gray-400">Obtén tu acreditación ante CONOCER para evaluar competencias laborales.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white/5 rounded-xl p-4">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Crea tu Centro de Evaluación</h4>
                    <p className="text-sm text-gray-400">Te acompañamos en el proceso de acreditación de tu propio centro.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white/5 rounded-xl p-4">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Handshake className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Alianzas Estratégicas</h4>
                    <p className="text-sm text-gray-400">Forma parte de nuestra red de centros y evaluadores certificados.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=500&fit=crop"
                alt="Formación profesional"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-gray-700" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Avalado por SEP</p>
                    <p className="text-sm text-gray-600">A través de CONOCER</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formularios Section */}
      <section className="py-16 lg:py-20 bg-gray-50" id="formularios">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
              <FileCheck className="h-3.5 w-3.5 mr-1.5" />
              Registro
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo podemos ayudarte?
            </h2>
            <p className="text-gray-600">
              Selecciona la opción que mejor describa tu interés y completa el formulario.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 h-auto p-1 bg-gray-200">
              <TabsTrigger 
                value="interesados" 
                className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 data-[state=active]:bg-white"
                data-testid="tab-interesados"
              >
                <UserPlus className="h-4 w-4" />
                <span className="text-xs sm:text-sm">Interesados</span>
              </TabsTrigger>
              <TabsTrigger 
                value="centros" 
                className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 data-[state=active]:bg-white"
                data-testid="tab-centros"
              >
                <Building2 className="h-4 w-4" />
                <span className="text-xs sm:text-sm">Centros</span>
              </TabsTrigger>
              <TabsTrigger 
                value="certificadores" 
                className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 data-[state=active]:bg-white"
                data-testid="tab-certificadores"
              >
                <Award className="h-4 w-4" />
                <span className="text-xs sm:text-sm">Certificadores</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab: Interesados en certificarse */}
            <TabsContent value="interesados">
              <Card className="border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gray-900 flex items-center justify-center">
                      <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    Quiero certificarme o tomar cursos
                  </CardTitle>
                  <p className="text-gray-600 text-sm mt-2">
                    Si estás interesado en obtener una certificación o tomar cursos de la Academia UPIM, del Colegio de Inmobiliarios o CONOCER, déjanos tus datos.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleInteresadoSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="int-nombre">Nombre completo *</Label>
                        <Input
                          id="int-nombre"
                          value={interesadoForm.nombre}
                          onChange={(e) => setInteresadoForm({...interesadoForm, nombre: e.target.value})}
                          placeholder="Tu nombre"
                          required
                          data-testid="input-interesado-nombre"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="int-email">Correo electrónico *</Label>
                        <Input
                          id="int-email"
                          type="email"
                          value={interesadoForm.email}
                          onChange={(e) => setInteresadoForm({...interesadoForm, email: e.target.value})}
                          placeholder="correo@ejemplo.com"
                          required
                          data-testid="input-interesado-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="int-telefono">Teléfono *</Label>
                        <Input
                          id="int-telefono"
                          value={interesadoForm.telefono}
                          onChange={(e) => setInteresadoForm({...interesadoForm, telefono: e.target.value})}
                          placeholder="55 1234 5678"
                          required
                          data-testid="input-interesado-telefono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="int-ciudad">Ciudad *</Label>
                        <Input
                          id="int-ciudad"
                          value={interesadoForm.ciudad}
                          onChange={(e) => setInteresadoForm({...interesadoForm, ciudad: e.target.value})}
                          placeholder="Ciudad de México"
                          required
                          data-testid="input-interesado-ciudad"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>¿Qué tipo de formación te interesa? *</Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {[
                          { value: 'curso_upim', label: 'Cursos Academia UPIM' },
                          { value: 'colegio', label: 'Cursos Colegio de Inmobiliarios' },
                          { value: 'conocer_ec0110', label: 'Certificación CONOCER EC0110.02' },
                          { value: 'conocer_otro', label: 'Otra certificación CONOCER' },
                        ].map((option) => (
                          <label 
                            key={option.value}
                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              interesadoForm.tipo_interes === option.value 
                                ? 'border-gray-900 bg-gray-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="tipo_interes"
                              value={option.value}
                              checked={interesadoForm.tipo_interes === option.value}
                              onChange={(e) => setInteresadoForm({...interesadoForm, tipo_interes: e.target.value})}
                              className="h-4 w-4 text-gray-900"
                            />
                            <span className="text-sm">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="int-mensaje">Mensaje adicional (opcional)</Label>
                      <Textarea
                        id="int-mensaje"
                        value={interesadoForm.mensaje}
                        onChange={(e) => setInteresadoForm({...interesadoForm, mensaje: e.target.value})}
                        placeholder="Cuéntanos más sobre tu interés o dudas..."
                        rows={3}
                        data-testid="input-interesado-mensaje"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gray-900 hover:bg-gray-800 h-12"
                      disabled={loading}
                      data-testid="btn-submit-interesado"
                    >
                      {loading ? 'Enviando...' : 'Enviar solicitud'}
                      <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Centros Certificadores */}
            <TabsContent value="centros">
              <Card className="border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gray-700 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    Centros Certificadores
                  </CardTitle>
                  <p className="text-gray-600 text-sm mt-2">
                    Si ya eres un Centro de Evaluación acreditado o deseas crear uno con el apoyo de UPIM, regístrate aquí.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCentroSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="centro-nombre">Nombre del Centro *</Label>
                        <Input
                          id="centro-nombre"
                          value={centroForm.nombre_centro}
                          onChange={(e) => setCentroForm({...centroForm, nombre_centro: e.target.value})}
                          placeholder="Centro de Evaluación XYZ"
                          required
                          data-testid="input-centro-nombre"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="centro-representante">Representante Legal *</Label>
                        <Input
                          id="centro-representante"
                          value={centroForm.representante}
                          onChange={(e) => setCentroForm({...centroForm, representante: e.target.value})}
                          placeholder="Nombre del representante"
                          required
                          data-testid="input-centro-representante"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="centro-email">Correo electrónico *</Label>
                        <Input
                          id="centro-email"
                          type="email"
                          value={centroForm.email}
                          onChange={(e) => setCentroForm({...centroForm, email: e.target.value})}
                          placeholder="contacto@centro.com"
                          required
                          data-testid="input-centro-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="centro-telefono">Teléfono *</Label>
                        <Input
                          id="centro-telefono"
                          value={centroForm.telefono}
                          onChange={(e) => setCentroForm({...centroForm, telefono: e.target.value})}
                          placeholder="55 1234 5678"
                          required
                          data-testid="input-centro-telefono"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="centro-ciudad">Ciudad *</Label>
                        <Input
                          id="centro-ciudad"
                          value={centroForm.ciudad}
                          onChange={(e) => setCentroForm({...centroForm, ciudad: e.target.value})}
                          placeholder="Ciudad"
                          required
                          data-testid="input-centro-ciudad"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="centro-estado">Estado *</Label>
                        <Input
                          id="centro-estado"
                          value={centroForm.estado}
                          onChange={(e) => setCentroForm({...centroForm, estado: e.target.value})}
                          placeholder="Estado"
                          required
                          data-testid="input-centro-estado"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="centro-web">Sitio web (opcional)</Label>
                      <Input
                        id="centro-web"
                        value={centroForm.sitio_web}
                        onChange={(e) => setCentroForm({...centroForm, sitio_web: e.target.value})}
                        placeholder="https://www.tucentro.com"
                        data-testid="input-centro-web"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="centro-descripcion">Descripción del Centro *</Label>
                      <Textarea
                        id="centro-descripcion"
                        value={centroForm.descripcion}
                        onChange={(e) => setCentroForm({...centroForm, descripcion: e.target.value})}
                        placeholder="Cuéntanos sobre tu centro, instalaciones y servicios..."
                        rows={3}
                        required
                        data-testid="input-centro-descripcion"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="centro-experiencia">Experiencia en certificación *</Label>
                      <Textarea
                        id="centro-experiencia"
                        value={centroForm.experiencia_certificacion}
                        onChange={(e) => setCentroForm({...centroForm, experiencia_certificacion: e.target.value})}
                        placeholder="¿Ya están acreditados ante CONOCER? ¿Qué estándares manejan? ¿Cuántos años de experiencia tienen?"
                        rows={3}
                        required
                        data-testid="input-centro-experiencia"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gray-700 hover:bg-gray-600 h-12"
                      disabled={loading}
                      data-testid="btn-submit-centro"
                    >
                      {loading ? 'Enviando...' : 'Registrar Centro'}
                      <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Certificadores/Evaluadores */}
            <TabsContent value="certificadores">
              <Card className="border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gray-500 flex items-center justify-center">
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    Evaluadores y Certificadores
                  </CardTitle>
                  <p className="text-gray-600 text-sm mt-2">
                    Si ya estás dado de alta como evaluador CONOCER o deseas darte de alta, regístrate para formar parte de nuestra red.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCertificadorSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cert-nombre">Nombre completo *</Label>
                        <Input
                          id="cert-nombre"
                          value={certificadorForm.nombre}
                          onChange={(e) => setCertificadorForm({...certificadorForm, nombre: e.target.value})}
                          placeholder="Tu nombre completo"
                          required
                          data-testid="input-certificador-nombre"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cert-email">Correo electrónico *</Label>
                        <Input
                          id="cert-email"
                          type="email"
                          value={certificadorForm.email}
                          onChange={(e) => setCertificadorForm({...certificadorForm, email: e.target.value})}
                          placeholder="correo@ejemplo.com"
                          required
                          data-testid="input-certificador-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cert-telefono">Teléfono *</Label>
                        <Input
                          id="cert-telefono"
                          value={certificadorForm.telefono}
                          onChange={(e) => setCertificadorForm({...certificadorForm, telefono: e.target.value})}
                          placeholder="55 1234 5678"
                          required
                          data-testid="input-certificador-telefono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cert-ciudad">Ciudad *</Label>
                        <Input
                          id="cert-ciudad"
                          value={certificadorForm.ciudad}
                          onChange={(e) => setCertificadorForm({...certificadorForm, ciudad: e.target.value})}
                          placeholder="Ciudad de México"
                          required
                          data-testid="input-certificador-ciudad"
                        />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-100 rounded-xl space-y-4">
                      <Label className="text-base font-semibold">Estatus ante CONOCER *</Label>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <label className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer flex-1 transition-all ${
                          certificadorForm.ya_registrado_conocer 
                            ? 'border-gray-900 bg-white' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="ya_registrado"
                            checked={certificadorForm.ya_registrado_conocer === true}
                            onChange={() => setCertificadorForm({...certificadorForm, ya_registrado_conocer: true})}
                            className="h-4 w-4 text-gray-900"
                          />
                          <div>
                            <span className="font-medium block">Ya estoy registrado</span>
                            <span className="text-xs text-gray-500">Tengo número de evaluador</span>
                          </div>
                        </label>
                        <label className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer flex-1 transition-all ${
                          certificadorForm.ya_registrado_conocer === false 
                            ? 'border-gray-900 bg-white' 
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}>
                          <input
                            type="radio"
                            name="ya_registrado"
                            checked={certificadorForm.ya_registrado_conocer === false}
                            onChange={() => setCertificadorForm({...certificadorForm, ya_registrado_conocer: false})}
                            className="h-4 w-4 text-gray-900"
                          />
                          <div>
                            <span className="font-medium block">Quiero registrarme</span>
                            <span className="text-xs text-gray-500">Necesito dar de alta</span>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {certificadorForm.ya_registrado_conocer && (
                      <div className="space-y-2">
                        <Label htmlFor="cert-numero">Número de Evaluador CONOCER</Label>
                        <Input
                          id="cert-numero"
                          value={certificadorForm.numero_conocer}
                          onChange={(e) => setCertificadorForm({...certificadorForm, numero_conocer: e.target.value})}
                          placeholder="Ej: EVAL-12345"
                          data-testid="input-certificador-numero"
                        />
                      </div>
                    )}
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cert-especialidad">Especialidad *</Label>
                        <Input
                          id="cert-especialidad"
                          value={certificadorForm.especialidad}
                          onChange={(e) => setCertificadorForm({...certificadorForm, especialidad: e.target.value})}
                          placeholder="Ej: Bienes raíces, valuación"
                          required
                          data-testid="input-certificador-especialidad"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cert-experiencia">Años de experiencia *</Label>
                        <Input
                          id="cert-experiencia"
                          type="number"
                          min="0"
                          value={certificadorForm.experiencia_anios}
                          onChange={(e) => setCertificadorForm({...certificadorForm, experiencia_anios: e.target.value})}
                          placeholder="Ej: 5"
                          required
                          data-testid="input-certificador-experiencia"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cert-mensaje">Información adicional</Label>
                      <Textarea
                        id="cert-mensaje"
                        value={certificadorForm.mensaje}
                        onChange={(e) => setCertificadorForm({...certificadorForm, mensaje: e.target.value})}
                        placeholder="Cuéntanos sobre tu experiencia, certificaciones actuales, o cualquier información relevante..."
                        rows={3}
                        data-testid="input-certificador-mensaje"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gray-500 hover:bg-gray-600 h-12"
                      disabled={loading}
                      data-testid="btn-submit-certificador"
                    >
                      {loading ? 'Enviando...' : 'Registrarme como Evaluador'}
                      <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-gray-100 text-gray-700 hover:bg-gray-100">
                <Phone className="h-3.5 w-3.5 mr-1.5" />
                Contacto Directo
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                ¿Tienes dudas sobre certificaciones?
              </h2>
              <p className="text-gray-600 mb-8">
                Nuestro equipo de formación académica está listo para asesorarte sobre la mejor ruta de certificación para ti o tu organización.
              </p>
              
              <div className="space-y-4">
                <a href="tel:+525555555555" className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group">
                  <div className="h-12 w-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Llámanos</p>
                    <p className="text-gray-600">+52 55 5555 5555</p>
                  </div>
                </a>
                
                <a href="mailto:academia@upim.org" className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group">
                  <div className="h-12 w-12 rounded-full bg-gray-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Escríbenos</p>
                    <p className="text-gray-600">academia@upim.org</p>
                  </div>
                </a>
                
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                  <div className="h-12 w-12 rounded-full bg-gray-500 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Visítanos</p>
                    <p className="text-gray-600">Ciudad de México, CDMX</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=500&fit=crop"
                alt="Equipo UPIM"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <Badge className="px-4 py-1.5 bg-white/10 text-white border border-white/20">
              <Star className="h-4 w-4 mr-1.5" />
              Únete a UPIM
            </Badge>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Impulsa tu carrera con certificaciones oficiales
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Forma parte de la comunidad de profesionales inmobiliarios más importante de México y accede a formación de primer nivel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-gray-900 hover:bg-gray-100 h-14 px-8"
              asChild
            >
              <Link to="/afiliacion" data-testid="cta-afiliacion">
                Afíliate ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10 h-14 px-8"
              asChild
            >
              <Link to="/contacto" data-testid="cta-contacto">
                Más información
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CertificacionesPage;
